import React, { useState, useEffect, useMemo } from 'react'
import * as api from '../../services/api.js'

const COMPANIONS = [
  { id: 'orinthia', name: 'Orinthia', emoji: '🦉', desc: 'Reads at exactly your pace — a mirror of your dedication.' },
  { id: 'miso',     name: 'Miso',     emoji: '🐱', desc: 'Always one book behind, but never gives up.' },
  { id: 'sable',    name: 'Sable',    emoji: '🦊', desc: 'Racing two books ahead — can you catch her?' },
  { id: 'humphrey', name: 'Humphrey', emoji: '🐻', desc: 'Two books back, steady and warm. He will get there.' },
]

const COMPANION_VOICES = {
  orinthia: {
    idle:     'Let us begin. A good story awaits us both.',
    leading:  'We move well together. Page by page.',
    trailing: 'You have slipped ahead. I am catching up.',
    tied:     'We are perfectly matched today.',
  },
  miso: {
    idle:     'I am absolutely going to read more. Soon.',
    leading:  'Wait for me! I am almost there!',
    trailing: 'Oh! You are faster than me today. Impressive.',
    tied:     'We are tied! How did that happen?',
  },
  sable: {
    idle:     'I am already two books ahead. No pressure.',
    leading:  'You are gaining on me. I respect that.',
    trailing: 'You are getting close. But not close enough.',
    tied:     'You caught up. Enjoy it. It will not last.',
  },
  humphrey: {
    idle:     'No rush. There is always another page tomorrow.',
    leading:  'Well done. You have raced past me.',
    trailing: 'I am still here. Plodding along, as always.',
    tied:     'Good. Steady does it.',
  },
}

const QUEST_POOL = [
  { id: 'fantasy',           label: 'Fantasy',              emoji: '🐉' },
  { id: 'epic-fantasy',      label: 'Epic Fantasy',         emoji: '⚔️' },
  { id: 'cosy-fantasy',      label: 'Cosy Fantasy',         emoji: '🏡' },
  { id: 'dark-fantasy',      label: 'Dark Fantasy',         emoji: '🌑' },
  { id: 'mystery',           label: 'Mystery',              emoji: '🔍' },
  { id: 'cozy-mystery',      label: 'Cosy Mystery',         emoji: '☕' },
  { id: 'crime',             label: 'Crime',                emoji: '🗝️' },
  { id: 'romance',           label: 'Romance',              emoji: '💌' },
  { id: 'historical-romance',label: 'Historical Romance',   emoji: '💐' },
  { id: 'historical',        label: 'Historical',           emoji: '📜' },
  { id: 'medieval',          label: 'Medieval',             emoji: '🏰' },
  { id: 'wwii',              label: 'World War II',         emoji: '🎖️' },
  { id: 'scifi',             label: 'Sci-Fi',               emoji: '🚀' },
  { id: 'space-opera',       label: 'Space Opera',          emoji: '🌌' },
  { id: 'dystopian',         label: 'Dystopian',            emoji: '🏗️' },
  { id: 'thriller',          label: 'Thriller',             emoji: '🗡️' },
  { id: 'psych-thriller',    label: 'Psychological Thriller', emoji: '🧠' },
  { id: 'literary',          label: 'Literary Fiction',     emoji: '🪶' },
  { id: 'coming-of-age',     label: 'Coming of Age',        emoji: '🌱' },
  { id: 'nonfiction',        label: 'Non-Fiction',          emoji: '🔬' },
  { id: 'memoir',            label: 'Memoir',               emoji: '✍️' },
  { id: 'nature-writing',    label: 'Nature Writing',       emoji: '🌿' },
  { id: 'horror',            label: 'Horror',               emoji: '👻' },
  { id: 'gothic-horror',     label: 'Gothic Horror',        emoji: '🕸️' },
  { id: 'poetry',            label: 'Poetry',               emoji: '🌙' },
  { id: 'ya',                label: 'Young Adult',          emoji: '🌟' },
  { id: 'graphic-novel',     label: 'Graphic Novel',        emoji: '🎨' },
  { id: 'short-stories',     label: 'Short Stories',        emoji: '✦' },
]

const GENRE_VAULT_KEYS = {
  fantasy: 'fantasy-gem', mystery: 'mystery-key', romance: 'romance-rose',
  historical: 'history-scroll', scifi: 'scifi-star', thriller: 'thriller-blade',
  literary: 'literary-quill', nonfiction: 'nonfiction-lens', poetry: 'poetry-moon',
}

const BINGO_PROMPT_POOL = [
  'Colour in title',    'Debut novel',          'Another country',    'Published this year',  'One-word title',
  'About friendship',   'Over 400 pages',       'Translated book',    'New-to-you author',    'Recommended read',
  'Map inside',         'A love story',         'FREE',               'Mystery or thriller',   'Nature or outdoors',
  'Under 200 pages',    'Written by a woman',   'Second in series',   'About family',         'Historical setting',
  'Beautiful cover',    'Book club pick',       'Food or cooking',    'A re-read',            'Non-English setting',
  'Set in a city',      'About grief',          'Published pre-2000', 'Award winner',         'Found family',
  'Enemies to lovers',  'About science',        'Written by a man',   'No chapters',          'Dual timeline',
  'Set in winter',      'About childhood',      'Unreliable narrator','Over 500 pages',       'Set in a school',
  'About animals',      'Local author',         'About travel',       'Verse or poetry',      'About music',
  'Magic realism',      'About politics',       'Female protagonist', 'Male protagonist',     'First in series',
  'Short story coll.',  'Set in Asia',          'Set in Africa',      'Gothic atmosphere',    'About class',
  'Told in letters',    'About art',            'Blind date book',    'Under 150 pages',      'Non-linear story',
]

const RACE_TARGET = 12

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function initQuestSlots() {
  return shuffle(QUEST_POOL.map(g => g.id)).slice(0, 9)
}

function initBingoPrompts() {
  const pool = BINGO_PROMPT_POOL.filter(p => p !== 'FREE')
  const chosen = shuffle(pool).slice(0, 24)
  return [...chosen.slice(0, 12), 'FREE', ...chosen.slice(12)]
}

const VAULT_ITEMS = [
  { key: 'first-quest',    name: 'The First Page',      icon: '📖', from: 'Start your first challenge' },
  { key: 'genre-master',   name: 'Genre Wanderer',      icon: '🗺️', from: 'Complete all 9 genres' },
  { key: 'bingo-row',      name: 'Row Maker',           icon: '🎯', from: 'Complete a row in Bingo' },
  { key: 'bingo-diagonal', name: 'Corner to Corner',    icon: '↗️', from: 'Complete a diagonal in Bingo' },
  { key: 'bingo-full',     name: 'Bingo Champion',      icon: '🏆', from: 'Complete a full Bingo card' },
  { key: 'race-start',     name: 'Ready, Set, Read',    icon: '🏁', from: 'Start a Character Race' },
  { key: 'race-win',       name: 'Sprinter',            icon: '🥇', from: 'Win a Character Race' },
  { key: 'fantasy-gem',    name: 'Fantasy Crystal',     icon: '💎', from: 'Read a Fantasy book' },
  { key: 'mystery-key',    name: 'Mystery Key',         icon: '🗝️', from: 'Read a Mystery book' },
  { key: 'romance-rose',   name: 'Blooming Rose',       icon: '🌹', from: 'Read a Romance book' },
  { key: 'history-scroll', name: 'Ancient Scroll',      icon: '📜', from: 'Read a Historical book' },
  { key: 'scifi-star',     name: 'Star Chart',          icon: '⭐', from: 'Read a Sci-Fi book' },
  { key: 'thriller-blade', name: 'Shadow Blade',        icon: '🗡️', from: 'Read a Thriller book' },
  { key: 'literary-quill', name: 'Ink Quill',           icon: '🪶', from: 'Read Literary Fiction' },
  { key: 'nonfiction-lens',name: 'Truth Lens',          icon: '🔭', from: 'Read a Non-Fiction book' },
  { key: 'poetry-moon',    name: 'Silver Moon',         icon: '🌙', from: 'Read a Poetry book' },
  { key: 'orinthia-bond',  name: "Orinthia's Feather",  icon: '🦉', from: 'Complete a challenge with Orinthia' },
  { key: 'miso-bond',      name: "Miso's Bell",         icon: '🔔', from: 'Complete a challenge with Miso' },
  { key: 'sable-bond',     name: "Sable's Tail",        icon: '🧡', from: 'Complete a challenge with Sable' },
  { key: 'humphrey-bond',  name: "Humphrey's Acorn",    icon: '🌰', from: 'Complete a challenge with Humphrey' },
  { key: 'five-books',     name: 'Handful of Tales',    icon: '✋', from: 'Log 5 books to challenges' },
  { key: 'ten-books',      name: 'Double Digits',       icon: '🎲', from: 'Read 10 books in a year' },
  { key: 'streak-week',    name: 'Week of Pages',       icon: '📅', from: 'Log sessions 7 days in a row' },
  { key: 'collector',      name: 'Vault Keeper',        icon: '🏺', from: 'Unlock 10 vault items' },
]

const CHALLENGE_TYPES = [
  { id: 'genre-quest',    label: 'Genre Quest',    emoji: '🗺️', desc: 'Read across 9 genres' },
  { id: 'character-race', label: 'Character Race', emoji: '🏃', desc: 'Race your companion' },
  { id: 'reading-bingo',  label: 'Reading Bingo',  emoji: '🎯', desc: '5×5 bingo grid' },
]

function getCompanionBooks(companionId, userBooks) {
  switch (companionId) {
    case 'orinthia': return userBooks
    case 'miso':     return Math.max(0, userBooks - 1)
    case 'sable':    return userBooks + 2
    case 'humphrey': return Math.max(0, userBooks - 2)
    default:         return userBooks
  }
}

const BINGO_ROWS  = [[0,1,2,3,4],[5,6,7,8,9],[10,11,12,13,14],[15,16,17,18,19],[20,21,22,23,24]]
const BINGO_COLS  = [[0,5,10,15,20],[1,6,11,16,21],[2,7,12,17,22],[3,8,13,18,23],[4,9,14,19,24]]
const BINGO_DIAGS = [[0,6,12,18,24],[4,8,12,16,20]]

export default function ReadingChallenge({ books }) {
  const [tab,        setTab]        = useState('challenge')
  const [challengeType, setType]    = useState('genre-quest')
  const [companion,  setCompanion]  = useState('orinthia')
  const [challenges, setChallenges] = useState([])
  const [vault,      setVault]      = useState([])
  const [animKey,    setAnimKey]    = useState(null)

  const year = new Date().getFullYear()

  useEffect(() => {
    api.getReadingChallenges(year).then(r => setChallenges(r || []))
    api.getReadingVault().then(r => setVault(r || []))
  }, [year])

  const completedCount = useMemo(
    () => books.filter(b => b.status === 'completed').length,
    [books]
  )

  const activeChallenge = challenges.find(
    c => c.status === 'active' && c.challenge_type === challengeType
  )
  const completedChallenges = challenges.filter(c => c.status === 'completed')

  const challengeData = useMemo(() => {
    if (!activeChallenge) return {}
    try {
      return typeof activeChallenge.data === 'string'
        ? JSON.parse(activeChallenge.data)
        : (activeChallenge.data || {})
    } catch { return {} }
  }, [activeChallenge])

  function earnVault(itemKey) {
    if (vault.find(v => v.item_key === itemKey)) return
    const item = VAULT_ITEMS.find(v => v.key === itemKey)
    if (!item) return
    const entry = { item_key: item.key, item_name: item.name, item_icon: item.icon, earned_from: item.from }
    setVault(prev => [...prev, entry])
    setAnimKey(itemKey)
    setTimeout(() => setAnimKey(null), 2200)
    api.saveReadingVaultItem(entry)
  }

  async function startChallenge() {
    const data = challengeType === 'genre-quest'
      ? { completed: [], questSlots: initQuestSlots() }
      : challengeType === 'character-race'
        ? { userBooks: 0 }
        : { bingoPrompts: initBingoPrompts(), marked: [12] }
    const entry = { challenge_type: challengeType, companion_id: companion, year, status: 'active', data }
    const res = await api.saveReadingChallenge(entry)
    const id  = res.id || Date.now()
    setChallenges(prev => [{ ...entry, id, data: JSON.stringify(data) }, ...prev])
    earnVault('first-quest')
    if (challengeType === 'character-race') earnVault('race-start')
  }

  async function updateData(newData) {
    if (!activeChallenge) return
    setChallenges(prev =>
      prev.map(c => c.id === activeChallenge.id
        ? { ...c, data: JSON.stringify(newData) }
        : c
      )
    )
    await api.updateReadingChallenge(activeChallenge.id, { data: newData })
  }

  function toggleGenre(genreId) {
    const current = challengeData.completed || []
    const isDone  = current.includes(genreId)
    const next    = isDone ? current.filter(g => g !== genreId) : [...current, genreId]
    updateData({ ...challengeData, completed: next })
    if (!isDone) {
      if (GENRE_VAULT_KEYS[genreId]) earnVault(GENRE_VAULT_KEYS[genreId])
      const slots = challengeData.questSlots || QUEST_POOL.map(g => g.id).slice(0, 9)
      if (slots.every(id => next.includes(id))) {
        earnVault('genre-master')
        earnVault(`${companion}-bond`)
      }
    }
  }

  function reshuffleQuest() {
    const done      = challengeData.completed || []
    const allIds    = QUEST_POOL.map(g => g.id)
    const available = shuffle(allIds.filter(id => !done.includes(id)))
    const needed    = 9
    let next
    if (available.length >= needed) {
      next = available.slice(0, needed)
    } else {
      const completed = shuffle(allIds.filter(id => done.includes(id)))
      next = [...available, ...completed].slice(0, needed)
    }
    updateData({ ...challengeData, questSlots: next })
  }

  function reshuffleBingo() {
    const current      = challengeData.bingoPrompts || BINGO_PROMPT_POOL.slice(0, 25)
    const markedIdxs   = new Set(challengeData.marked || [12])
    const markedTexts  = new Set([...markedIdxs].map(i => current[i]).filter(Boolean))
    const pool         = BINGO_PROMPT_POOL.filter(p => p !== 'FREE')
    const mustKeep     = [...markedTexts].filter(t => t !== 'FREE')
    const canAdd       = shuffle(pool.filter(t => !mustKeep.includes(t)))
    const newNon12     = shuffle([...mustKeep, ...canAdd].slice(0, 24))
    const newPrompts   = [...newNon12.slice(0, 12), 'FREE', ...newNon12.slice(12)]
    const newMarked    = newPrompts.reduce((acc, p, i) => { if (markedTexts.has(p)) acc.push(i); return acc }, [])
    updateData({ ...challengeData, bingoPrompts: newPrompts, marked: newMarked })
  }

  function incrementRace() {
    const userBooks    = (challengeData.userBooks || 0) + 1
    const compBooks    = getCompanionBooks(companion, userBooks)
    updateData({ ...challengeData, userBooks })
    if (userBooks >= RACE_TARGET && compBooks < RACE_TARGET) earnVault('race-win')
    if (userBooks === RACE_TARGET) {
      earnVault(`${companion}-bond`)
    }
    if (userBooks >= 5)  earnVault('five-books')
    if (completedCount >= 10) earnVault('ten-books')
  }

  function toggleBingo(idx) {
    const prompts  = challengeData.bingoPrompts || BINGO_PROMPT_POOL.slice(0, 25)
    if (prompts[idx] === 'FREE') return
    const prev    = challengeData.marked || [12]
    const isMarked = prev.includes(idx)
    const next    = isMarked ? prev.filter(i => i !== idx) : [...prev, idx]
    updateData({ ...challengeData, marked: next })

    if (!isMarked) {
      const newSet  = new Set(next)
      const prevSet = new Set(prev)
      const allLines = [...BINGO_ROWS, ...BINGO_COLS, ...BINGO_DIAGS]
      for (const line of allLines) {
        if (line.every(i => newSet.has(i)) && !line.every(i => prevSet.has(i))) {
          earnVault('bingo-row')
          if (BINGO_DIAGS.some(d => d.every(i => newSet.has(i)))) earnVault('bingo-diagonal')
          break
        }
      }
      if (next.length === 25) {
        earnVault('bingo-full')
        earnVault(`${companion}-bond`)
      }
    }
  }

  const companionObj     = COMPANIONS.find(c => c.id === companion)
  const challengeTypeObj = CHALLENGE_TYPES.find(c => c.id === challengeType)
  const earnedCount      = vault.length

  return (
    <div>
      {/* Top tabs */}
      <div className="challenge-tabs">
        {[
          { id: 'challenge',  label: 'Challenge' },
          { id: 'vault',      label: `Vault (${earnedCount}/24)` },
          { id: 'completed',  label: `Completed (${completedChallenges.length})` },
        ].map(t => (
          <button
            key={t.id}
            className={`challenge-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'challenge' && (
        <>
          {/* Challenge type picker */}
          <div className="challenge-type-row">
            {CHALLENGE_TYPES.map(ct => (
              <button
                key={ct.id}
                className={`challenge-type-card${challengeType === ct.id ? ' active' : ''}`}
                onClick={() => setType(ct.id)}
              >
                <span className="challenge-type-emoji">{ct.emoji}</span>
                <span className="challenge-type-label">{ct.label}</span>
                <span className="challenge-type-desc">{ct.desc}</span>
              </button>
            ))}
          </div>

          {/* Companion picker */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="challenge-section-label">Your companion</div>
            <div className="companion-row">
              {COMPANIONS.map(comp => (
                <button
                  key={comp.id}
                  className={`companion-card${companion === comp.id ? ' active' : ''}`}
                  onClick={() => setCompanion(comp.id)}
                >
                  <span className="companion-emoji">{comp.emoji}</span>
                  <span className="companion-name">{comp.name}</span>
                </button>
              ))}
            </div>
            {companionObj && (
              <p className="companion-desc">{companionObj.desc}</p>
            )}
          </div>

          {!activeChallenge ? (
            <div style={{ textAlign: 'center', padding: '1.75rem 0' }}>
              <p style={{ fontSize: '0.88rem', color: 'var(--stone)', marginBottom: '1.25rem', fontFamily: 'var(--font-serif)', fontStyle: 'italic', lineHeight: 1.65 }}>
                No active {challengeTypeObj?.label} yet.{' '}
                Start one and {companionObj?.name} will join you.
              </p>
              <button className="challenge-start-btn" onClick={startChallenge}>
                Start challenge
              </button>
            </div>
          ) : (
            <div className="challenge-active-card">
              <div className="challenge-active-header">
                <span>{challengeTypeObj?.emoji} {challengeTypeObj?.label}</span>
                <span className="challenge-companion-badge">
                  {companionObj?.emoji} {companionObj?.name}
                </span>
              </div>

              {challengeType === 'genre-quest' && (
                <GenreQuest data={challengeData} onToggle={toggleGenre} onReshuffle={reshuffleQuest} />
              )}
              {challengeType === 'character-race' && (
                <CharacterRace
                  data={challengeData}
                  companion={companion}
                  voices={COMPANION_VOICES[companion]}
                  onIncrement={incrementRace}
                />
              )}
              {challengeType === 'reading-bingo' && (
                <ReadingBingo data={challengeData} onToggle={toggleBingo} onReshuffle={reshuffleBingo} />
              )}
            </div>
          )}
        </>
      )}

      {tab === 'vault' && (
        <VaultTab items={VAULT_ITEMS} earned={vault} animKey={animKey} />
      )}

      {tab === 'completed' && (
        <CompletedTab challenges={completedChallenges} />
      )}
    </div>
  )
}

function GenreQuest({ data, onToggle, onReshuffle }) {
  const completed  = data.completed || []
  const slotIds    = data.questSlots || QUEST_POOL.map(g => g.id).slice(0, 9)
  const slots      = slotIds.map(id => QUEST_POOL.find(g => g.id === id)).filter(Boolean)
  const slotsDone  = slots.filter(g => completed.includes(g.id)).length

  return (
    <div>
      <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem', lineHeight: 1.6 }}>
        Mark genres you have read a book in. Complete all 9 to earn the Genre Wanderer loot.
      </p>
      <div className="genre-grid">
        {slots.map(g => (
          <button
            key={g.id}
            className={`genre-cell${completed.includes(g.id) ? ' done' : ''}`}
            onClick={() => onToggle(g.id)}
          >
            <span className="genre-cell-emoji">{g.emoji}</span>
            <span className="genre-cell-label">{g.label}</span>
            {completed.includes(g.id) && <span className="genre-cell-check">✓</span>}
          </button>
        ))}
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--stone)' }}>{slotsDone}/9 completed</span>
        <button className="quest-reshuffle-btn" onClick={onReshuffle}>↺ New selection</button>
      </div>
    </div>
  )
}

function CharacterRace({ data, companion, voices, onIncrement }) {
  const userBooks  = data.userBooks || 0
  const compBooks  = getCompanionBooks(companion, userBooks)
  const userPct    = Math.min((userBooks / RACE_TARGET) * 100, 100)
  const compPct    = Math.min((compBooks / RACE_TARGET) * 100, 100)
  const compObj    = COMPANIONS.find(c => c.id === companion)
  const status     = userBooks > compBooks ? 'leading' : userBooks < compBooks ? 'trailing' : 'tied'
  const message    = voices[userBooks === 0 ? 'idle' : status]

  return (
    <div>
      <p style={{ fontSize: '0.88rem', color: 'var(--bark)', marginBottom: '1.25rem', fontStyle: 'italic', fontFamily: 'var(--font-serif)', lineHeight: 1.6 }}>
        &ldquo;{message}&rdquo;
        <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--stone)', fontStyle: 'normal', marginTop: 4 }}>— {compObj?.name}</span>
      </p>
      <div className="race-track">
        <div className="race-row">
          <span className="race-label">You</span>
          <div className="race-bar-wrap">
            <div className="race-bar you" style={{ width: `${userPct}%` }} />
          </div>
          <span className="race-count">{userBooks}</span>
        </div>
        <div className="race-row">
          <span className="race-label">{compObj?.name}</span>
          <div className="race-bar-wrap">
            <div className="race-bar companion" style={{ width: `${compPct}%` }} />
          </div>
          <span className="race-count">{compBooks}</span>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
        <button className="challenge-start-btn" onClick={onIncrement} disabled={userBooks >= RACE_TARGET}>
          + Log a book
        </button>
        <div style={{ fontSize: '0.72rem', color: 'var(--stone)', marginTop: '0.5rem' }}>
          Target: {RACE_TARGET} books
        </div>
      </div>
    </div>
  )
}

function ReadingBingo({ data, onToggle, onReshuffle }) {
  const prompts = data.bingoPrompts || BINGO_PROMPT_POOL.slice(0, 25)
  const marked  = new Set(data.marked || [12])

  return (
    <div>
      <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1rem', lineHeight: 1.6 }}>
        Read a book that fits each prompt and mark it off. Get a full row, column, or diagonal to win.
      </p>
      <div className="bingo-grid">
        {prompts.map((prompt, idx) => (
          <button
            key={idx}
            className={`bingo-cell${marked.has(idx) ? ' marked' : ''}${prompt === 'FREE' ? ' free' : ''}`}
            onClick={() => onToggle(idx)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--stone)' }}>{marked.size - 1}/24 marked</span>
        <button className="quest-reshuffle-btn" onClick={onReshuffle}>↺ New card</button>
      </div>
    </div>
  )
}

function VaultTab({ items, earned, animKey }) {
  const earnedKeys = new Set(earned.map(e => e.item_key))
  return (
    <div>
      <p style={{ fontSize: '0.83rem', color: 'var(--stone)', marginBottom: '1.25rem', fontStyle: 'italic', fontFamily: 'var(--font-serif)', lineHeight: 1.6 }}>
        {earned.length} of 24 items unlocked. Complete challenges to earn loot.
      </p>
      <div className="vault-grid">
        {items.map(item => {
          const isEarned = earnedKeys.has(item.key)
          return (
            <div
              key={item.key}
              className={`vault-item${isEarned ? ' earned' : ' locked'}${animKey === item.key ? ' anim' : ''}`}
            >
              <span className="vault-item-icon">{isEarned ? item.icon : '🔒'}</span>
              <span className="vault-item-name">{isEarned ? item.name : '???'}</span>
              <span className="vault-item-from">{item.from}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CompletedTab({ challenges }) {
  if (challenges.length === 0) {
    return (
      <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', padding: '1.75rem 0', fontFamily: 'var(--font-serif)', lineHeight: 1.65 }}>
        No completed challenges yet. Finish one and it will appear here.
      </p>
    )
  }
  const TYPE_LABELS = { 'genre-quest': 'Genre Quest', 'character-race': 'Character Race', 'reading-bingo': 'Reading Bingo' }
  const COMP_EMOJI  = { orinthia: '🦉', miso: '🐱', sable: '🦊', humphrey: '🐻' }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {challenges.map(c => (
        <div key={c.id} className="completed-challenge-row">
          <span className="completed-challenge-name">
            {TYPE_LABELS[c.challenge_type] || c.challenge_type}
          </span>
          <span className="completed-challenge-meta">
            {COMP_EMOJI[c.companion_id] || ''} {c.year}
          </span>
        </div>
      ))}
    </div>
  )
}
